/**
 * Club-set membership fees — the JOINING amount and the RENEWAL amount, per
 * tier. Some clubs charge a different first payment (kit, registration) from
 * the yearly renewal, so the two are separate numbers, and only the club's
 * admin sets them (owner's direction, 2 Sep 2026).
 *
 * The platform's TIERS fees are only the DEFAULT: a club that has never
 * opened the fees screen keeps charging exactly what it always did. Stored on
 * the club as a jsonb map `{ tier: { join, renew } }` and resolved through
 * clubFees() so every reader sees a complete map.
 *
 * WHICH of the two a member owes is decided by their record, never by the
 * caller: a membership that has NEVER paid (duesPaidTill null) owes the
 * joining amount; everyone else — lapsed included — owes the renewal. A life
 * member owes the joining amount: their one payment IS the joining payment.
 */

import { TIERS } from './constants';

/** The two amounts a club sets. Fixed vocabulary — see the header. */
export const FEE_KINDS = ['join', 'renew'];

/** Nobody's club fee is a crore. Catches a pasted phone number. */
export const FEE_MAX = 1_000_000;

const cleanAmount = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n <= FEE_MAX ? Math.round(n) : fallback;
};

/**
 * Resolves a stored (possibly partial, possibly null) override map into a
 * complete `{ tier: { join, renew } }` map. Unknown tiers are dropped; junk,
 * zero or negative values fall back to the tier's default — a fee is never
 * silently free because of a typo.
 */
export function clubFees(raw) {
  const out = {};
  for (const tier of Object.keys(TIERS)) {
    const o = raw?.[tier] ?? {};
    out[tier] = {
      join: cleanAmount(o.join, TIERS[tier].fee),
      renew: cleanAmount(o.renew, TIERS[tier].fee),
    };
  }
  return out;
}

/** The amount for one tier and kind, from a raw or resolved map. */
export function feeFor(raw, tier, kind) {
  const fees = clubFees(raw);
  const t = fees[tier] ?? fees.annual;
  return kind === 'join' ? t.join : t.renew;
}

/** 'join' for a membership that has never paid (life always), else 'renew'. */
export function feeKindFor(member) {
  const tier = member.tier ?? 'annual';
  if ((TIERS[tier] ?? TIERS.annual).months === null) return 'join';
  return member.duesPaidTill == null ? 'join' : 'renew';
}

/** What THIS member owes right now, under this club's fees. */
export function memberFee(raw, member) {
  return feeFor(raw, member.tier ?? 'annual', feeKindFor(member));
}

/**
 * Validates an edited map before it is saved. Returns a message naming the
 * offending field, or null when the edit is safe to store.
 */
export function feesError(input) {
  if (input == null || typeof input !== 'object') return 'Nothing to save.';
  for (const tier of Object.keys(TIERS)) {
    const o = input[tier];
    if (o == null) continue;
    for (const kind of FEE_KINDS) {
      if (o[kind] == null || o[kind] === '') continue;
      const n = Number(o[kind]);
      if (!Number.isFinite(n) || n <= 0 || n > FEE_MAX) {
        return (
          `${TIERS[tier].label} ${kind === 'join' ? 'joining' : 'renewal'} amount ` +
          `must be between ₹1 and ₹${FEE_MAX.toLocaleString('en-IN')}.`
        );
      }
    }
  }
  return null;
}

/* -------------------------------------------------- collecting the money -- */

/**
 * Which manual payment methods a club has actually published, in the order a
 * member should see them. UPI first: it is how most members pay.
 *
 * A method is offered only when there is something to SHOW for it — a tab that
 * opens onto an empty panel is worse than no tab.
 */
export function paymentMethods({ upi, qrUrl, holder, account, ifsc } = {}) {
  const list = [];
  if (upi || qrUrl) list.push('upi');
  if (holder || account || ifsc) list.push('bank');
  return list;
}

/**
 * Can this club actually take money on a public pay link?
 *
 * The question the pay page and the send-link sheet both need. A club in
 * gateway mode needs its Razorpay keys; a manual club needs at least one
 * published detail. False means a payment link is a dead end: it tells the
 * member what they owe and gives them nowhere to send it.
 */
export function clubCanCollect(meta = {}) {
  if (meta.payMode === 'razorpay') return !!meta.rpConfigured;
  return (
    paymentMethods({
      upi: meta.bankUpi,
      qrUrl: meta.bankQrUrl,
      holder: meta.bankHolder,
      account: meta.bankAccount,
      ifsc: meta.bankIfsc,
    }).length > 0
  );
}
